/**
 * Drift detection and realignment.
 * 
 * Detects misalignment between published snapshot ordering and current item execution status.
 */

import type { PrioritySnapshot } from "./priorityTruth";
import type { Item } from "../harmonizer/types";

/**
 * Drift report for a map/snapshot.
 * 
 * Identifies items that are misaligned:
 * - Above the cut line but not started (should be in progress/done)
 * - Below the cut line but in progress or done (should not be started)
 */
export interface DriftReport {
  snapshotId: string;
  mapId: string;
  metrics: {
    aboveLineNotStarted: number;
    belowLineInProgressOrDone: number;
    totalAboveLine: number;
    totalBelowLine: number;
  };
  misalignedItemsAboveLine: string[]; // itemIds that are above line but not started
  misalignedItemsBelowLine: string[]; // itemIds that are below line but in progress or done
}

/**
 * Item execution status.
 * Used to determine if an item is aligned with its priority position.
 */
export type ItemExecutionStatus = "not-started" | "in-progress" | "done";

/**
 * Computes drift between a published snapshot and current item execution status.
 * 
 * Drift is defined as misalignment between:
 * - The published snapshot ordering (above/below cut line)
 * - Current item execution status (done, in-progress, not-started)
 * 
 * @param publishedSnapshot - The published priority snapshot
 * @param items - Current items with execution status
 * @param cutLine - Cut line position (items above this are "above the line")
 *                 If not provided, uses a simple midpoint or treats all as "above line"
 * @returns Drift report with metrics and misaligned items
 */
export function computeDrift(
  publishedSnapshot: PrioritySnapshot,
  items: Item[],
  cutLine?: number
): DriftReport {
  const scores = publishedSnapshot.scoreSet.scores;
  
  // Determine cut line
  // If not provided, use midpoint or treat all as above line
  const effectiveCutLine = cutLine ?? Math.ceil(scores.length / 2);
  
  // Create a map of itemId -> rank for quick lookup
  const itemRankMap = new Map<string, number>();
  scores.forEach((score) => {
    if (score.rank !== undefined) {
      itemRankMap.set(score.itemId, score.rank);
    }
  });
  
  // Sort items by rank (lower rank = higher priority)
  const sortedItems = items
    .filter((item) => itemRankMap.has(item.id))
    .sort((a, b) => {
      const rankA = itemRankMap.get(a.id) ?? Infinity;
      const rankB = itemRankMap.get(b.id) ?? Infinity;
      return rankA - rankB;
    });
  
  // Determine execution status for each item
  // For now, we'll infer from item attributes or use a default
  // In a full implementation, this would come from the item's lifecycle state
  const getItemStatus = (item: Item): ItemExecutionStatus => {
    // Check if item has execution status in attributes
    if (item.attributes) {
      const status = item.attributes.status || item.attributes.executionStatus;
      if (status === "done" || status === "completed") return "done";
      if (status === "in-progress" || status === "in_progress" || status === "active") return "in-progress";
    }
    
    // Default: item in list (contextual priority) and in execution stage
    const o = item as { itemPoolId?: string };
    const inList = !!o.itemPoolId;
    if (inList && item.lifecycle_stage === "execution") {
      if ((item as { aboveTheLine?: boolean }).aboveTheLine) {
        return "in-progress";
      }
    }
    
    return "not-started";
  };
  
  // Categorize items
  const aboveLineItems: Item[] = [];
  const belowLineItems: Item[] = [];
  const misalignedAbove: string[] = [];
  const misalignedBelow: string[] = [];
  
  sortedItems.forEach((item, index) => {
    const isAboveLine = index < effectiveCutLine;
    const status = getItemStatus(item);
    
    if (isAboveLine) {
      aboveLineItems.push(item);
      // Above line items should be in-progress or done
      if (status === "not-started") {
        misalignedAbove.push(item.id);
      }
    } else {
      belowLineItems.push(item);
      // Below line items should not be started
      if (status === "in-progress" || status === "done") {
        misalignedBelow.push(item.id);
      }
    }
  });
  
  return {
    snapshotId: publishedSnapshot.snapshotId,
    mapId: publishedSnapshot.sessionId, // Using sessionId as mapId for now
    metrics: {
      aboveLineNotStarted: misalignedAbove.length,
      belowLineInProgressOrDone: misalignedBelow.length,
      totalAboveLine: aboveLineItems.length,
      totalBelowLine: belowLineItems.length,
    },
    misalignedItemsAboveLine: misalignedAbove,
    misalignedItemsBelowLine: misalignedBelow,
  };
}


